import { Box, DialogContent, Stack } from "@mui/material";
import { logEvent } from "@src/appV2/lib/analytics";
import type { ButtonProps } from "@src/appV2/redesign/components/Button";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { TopLeftBackButton } from "@src/appV2/redesign/components/TopLeftBackButton";
import { TopRightCloseButton } from "@src/appV2/redesign/components/TopRightCloseButton";
import {
  BottomSheet,
  LoadingSpinner,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useCreateShiftTransfer } from "@src/appV2/Shifts/ShiftTransfer/api/useCreateShiftTransfer";
import { USER_EVENTS } from "@src/constants";
import { useMemo, useState } from "react";

import {
  type TransferShiftQuizQuestion,
  TransferShiftQuizQuestionCard,
} from "../utils/QuizQuestionCard";
import { namePlaceholder, replaceNames } from "../utils/replaceNames";
import { ConfirmTransferer } from "./ConfirmTransferer";
import { type ChosenFriend, EligibleFriendChooser } from "./EligibleFriendChooser";
import { SentRequestConfirmation } from "./SentRequestConfirmation";
import { SentRequestError } from "./SentRequestError";
import { TransfererRulesInfo } from "./TransfererRulesInfo";

const quizQuestions: TransferShiftQuizQuestion[] = [
  {
    question: `If ${namePlaceholder} accepts the transfer and cancels 30 hours before start, then ...`,
    correctAnswer: `My Clipboard Score will be penalized by 30 points AND ${namePlaceholder}'s Clipboard Score will be penalized by 40 points`,
    wrongAnswers: [
      "My Clipboard Score will not be affected",
      `${namePlaceholder}'s Clipboard Score will not be affected`,
    ],
    onWrongMessage:
      "Try again! If the transfer recipient cancels a transfer shift or No Call No Shows, you will lose 30 points from your Clipboard Score.",
    onCorrectMessage: `That's right! You will lose 30 points if ${namePlaceholder} cancels or No Call No Shows.
    You will not be affected by any lateness penalties.`,
  },
  {
    question: `If ${namePlaceholder} accepts the transfer and works the shift, then...`,
    correctAnswer: `My Clipboard Score will remain the same and ${namePlaceholder}'s Clipboard Score will increase by 10 points`,
    wrongAnswers: [
      `${namePlaceholder}'s Clipboard Score will remain the same`,
      "My Clipboard Score will increase by 10 points",
    ],
    onWrongMessage:
      "Try again! Hint: If your friend works the shift, only your friend gets points for working the shift.",
    onCorrectMessage: `That's right! Your score will remain the same if ${namePlaceholder} works the shift. Only ${namePlaceholder} gets points for working the shift.`,
  },
];

interface TransferShiftBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
}

/**
 * flow to offer transferring a booked shift, to another worker
 */
export function TransferShiftBottomSheet(props: TransferShiftBottomSheetProps) {
  const { modalState, shiftId } = props;

  // track flow progress
  const [confirmedTransferShiftInfo, setConfirmedTransferShiftInfo] = useState(false);
  const [chosenFriend, setChosenFriend] = useState<undefined | ChosenFriend>(undefined);
  const [correctQuizQuestions, setCorrectQuizQuestions] = useState(0);
  const [requestSent, setRequestSent] = useState(false);

  function resetFlowProgress() {
    setConfirmedTransferShiftInfo(false);
    setChosenFriend(undefined);
    setCorrectQuizQuestions(0);
    setRequestSent(false);
  }

  const questions = useMemo(() => {
    return quizQuestions.map((element) =>
      replaceNames(element, chosenFriend?.attributes.firstName ?? "transfer recipient")
    );
  }, [chosenFriend]);

  const { mutate, isLoading, isError } = useCreateShiftTransfer();

  function createShiftTransfer() {
    if (chosenFriend) {
      mutate({ shiftId, payload: { toWorkerId: chosenFriend.id } });
    }

    setRequestSent(true);
  }

  const actionButtonProps: ButtonProps = {
    fullWidth: true,
    variant: "brand",
    size: "large",
  };
  let dialogTitle: string;
  let dialogContent: JSX.Element;
  let actionButton: JSX.Element | undefined;
  let cancelButton: JSX.Element;

  if (!confirmedTransferShiftInfo) {
    // step 1: let user confirm transfer rules
    dialogTitle = "Only transfer to reliable professionals";
    dialogContent = <TransfererRulesInfo />;
    cancelButton = (
      <TopRightCloseButton
        onClick={() => {
          modalState.closeModal();
        }}
      />
    );
    actionButton = (
      <Button
        key="continue-button"
        {...actionButtonProps}
        onClick={() => {
          setConfirmedTransferShiftInfo(true);
        }}
      >
        Pick a Friend
      </Button>
    );
  } else if (!chosenFriend) {
    // step 2: let user choose pick and confirm an eligable friend
    dialogTitle = "Select a professional";
    dialogContent = (
      <EligibleFriendChooser
        shiftId={shiftId}
        onFriendChosen={(friend) => {
          setChosenFriend(friend);
        }}
      />
    );
    cancelButton = (
      <TopLeftBackButton
        onClick={() => {
          setConfirmedTransferShiftInfo(false);
        }}
      />
    );
  } else if (correctQuizQuestions < questions.length) {
    // step 3: answer quiz
    dialogTitle = "Pre-transfer quiz";
    dialogContent = (
      <TransferShiftQuizQuestionCard
        quizQuestion={questions[correctQuizQuestions]}
        questionNumber={correctQuizQuestions + 1}
        totalQuestions={questions.length}
        onCorrectAnswer={() => {
          setCorrectQuizQuestions(correctQuizQuestions + 1);
        }}
      />
    );
    cancelButton = (
      <TopLeftBackButton
        onClick={() => {
          setChosenFriend(undefined);
        }}
      />
    );
  } else if (!requestSent) {
    // step 4: Confirm Transfer
    dialogTitle = "Confirm transfer";

    dialogContent = isLoading ? (
      <Stack alignItems="center">
        <LoadingSpinner size="large" variant="primary" />
      </Stack>
    ) : (
      <ConfirmTransferer transferee={chosenFriend} />
    );

    cancelButton = (
      <TopLeftBackButton
        disabled={isLoading}
        onClick={() => {
          setChosenFriend(undefined);
        }}
      />
    );
    if (!isLoading) {
      actionButton = (
        <Button
          {...actionButtonProps}
          onClick={() => {
            logEvent(USER_EVENTS.SHIFT_TRANSFERER_SEND_REQUEST, {
              shiftId,
              toWorkerId: chosenFriend.id,
            });
            createShiftTransfer();
          }}
        >
          Yes, Send request to Transfer this Shift
        </Button>
      );
    }
  } else if (isError) {
    // step 5a: Show Error Message
    dialogTitle = `Shift transfer to ${chosenFriend.attributes.firstName} ${chosenFriend.attributes.lastName} failed`;
    dialogContent = <SentRequestError />;
    cancelButton = (
      <TopRightCloseButton
        onClick={() => {
          resetFlowProgress();
          modalState.closeModal();
        }}
      />
    );
  } else {
    // step 5b: Show Sucess
    dialogTitle = `Request sent to ${chosenFriend.attributes.firstName} ${chosenFriend.attributes.lastName}`;
    dialogContent = <SentRequestConfirmation transfereeName={chosenFriend.attributes.firstName} />;
    cancelButton = (
      <TopRightCloseButton
        onClick={() => {
          resetFlowProgress();
          modalState.closeModal();
        }}
      />
    );
  }

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        actionButton ? (
          <DialogFooter
            orientation="vertical"
            sx={{ marginBottom: 4 }}
            closeButtonAriaLabel="Close shift transfer"
          >
            {actionButton}
          </DialogFooter>
        ) : (
          <Box sx={{ minHeight: 64 }} />
        )
      }
    >
      <DialogContent sx={{ backgroundColor: "unset", py: 8, mt: 8 }}>
        <Stack direction="column" alignItems="center" spacing={9}>
          {cancelButton}
          <Title component="h3" variant="h3" sx={{ textAlign: "center", textWrap: "balance" }}>
            {dialogTitle}
          </Title>
          {dialogContent}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
